import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/site/Logo";
import { NAV } from "@/lib/site";

/**
 * Site-wide top navigation.
 * - Variant `light` = for dark hero pages
 * - Variant `dark`  = for white/light pages
 */
export default function Nav({ variant = "dark" }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const isLight = variant === "light";
  const barBg = isLight
    ? scrolled
      ? "bg-black/80 backdrop-blur-md border-b border-white/10"
      : "bg-[#050609]"
    : scrolled
    ? "bg-white/85 backdrop-blur-md border-b border-black/10"
    : "bg-white/60 backdrop-blur-md border-b border-transparent";

  const linkBase = isLight ? "text-white/70 hover:text-white" : "text-black/60 hover:text-black";
  const linkActive = isLight ? "text-white" : "text-black";

  return (
    <header
      data-testid="site-nav"
      className={`sticky top-0 z-50 w-full transition-all ${barBg}`}
    >
      <div className="container-lw flex h-16 items-center justify-between md:h-20">
        <Link to="/" data-testid="nav-home-logo" aria-label="LotWise Auto home">
          <Logo variant={isLight ? "light" : "dark"} />
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-tight transition ${
                  isActive ? linkActive : linkBase
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <Link
            to="/request-a-visit"
            data-testid="nav-request-visit-cta"
            className={
              isLight
                ? "btn-primary-lw"
                : "btn-primary-lw"
            }
          >
            Request a Visit
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        <button
          type="button"
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
          className={`md:hidden rounded-md p-2 ${
            isLight ? "text-white" : "text-black"
          }`}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className={`md:hidden ${
            isLight
              ? "border-t border-white/10 bg-black text-white"
              : "border-t border-black/10 bg-white text-black"
          }`}
        >
          <div className="container-lw flex flex-col gap-1 py-4">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-mobile-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-md px-2 py-3 text-base font-medium hover:bg-white/5"
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/request-a-visit"
              data-testid="nav-mobile-request-visit-cta"
              className="mt-2 btn-primary-lw"
            >
              Request a Visit <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
